"use client";

import { useEffect, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { usePathname } from "next/navigation";
import Link from "next/link";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import Divider from "./Divider";
import { getLocaleFromPath } from "../lib/i18n/utils";

export default function WhereSection() {
  const { t } = useTranslation();
  const pathname = usePathname();
  const locale = getLocaleFromPath(pathname);
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const marker = useRef<maplibregl.Marker | null>(null);

  const mapTilerApiKey = process.env.NEXT_PUBLIC_MAPTILER_KEY;
  const mapStyle = mapTilerApiKey
    ? `https://api.maptiler.com/maps/streets-v2/style.json?key=${mapTilerApiKey}`
    : "https://demotiles.maplibre.org/style.json";

  useEffect(() => {
    if (map.current || !mapContainer.current) return;

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: mapStyle,
      center: [21.163365, 40.499560],
      zoom: 15,
      bearing: 0,
      pitch: 0,
      dragRotate: false,
      touchPitch: false,
    });

    const handleResize = () => {
      if (map.current) {
        map.current.resize();
      }
    };

    window.addEventListener("resize", handleResize);

    map.current.on("load", () => {
      const el = document.createElement("div");
      el.style.width = "2.75rem";
      el.style.height = "2.75rem";
      el.style.backgroundImage = "url(/pin.svg)";
      el.style.backgroundSize = "contain";
      el.style.backgroundRepeat = "no-repeat";
      el.style.backgroundPosition = "center";
      el.style.filter = "drop-shadow(0 0.125rem 0.25rem rgba(0, 0, 0, 0.2))";

      marker.current = new maplibregl.Marker({
        element: el,
        anchor: "bottom",
      })
        .setLngLat([21.163365, 40.499560])
        .addTo(map.current!);
    });

    map.current.on("error", (e) => {
      console.error("Map error:", e);
    });

    setTimeout(() => {
      if (map.current) {
        map.current.resize();
      }
    }, 100);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (marker.current) {
        marker.current.remove();
        marker.current = null;
      }
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [mapStyle]);

  return (
    <section className="w-full">
      <div className="grid grid-cols-1 lg:grid-cols-2">
        <div className="bg-ui-card flex items-center px-5 py-12 md:px-8 md:py-16 lg:px-12 lg:py-20 xl:px-32 order-1 lg:order-2">
          <div className="w-full text-left">
            <h1 className="text-[clamp(2rem,5vw,3rem)] font-normal leading-[100%] text-white mb-4 md:mb-6 lg:mb-[1.5625rem]" suppressHydrationWarning>
              {t("Where we are")}
            </h1>

            <Divider className="mx-0 w-full !mt-0 !mb-0" />

            <p className="mt-4 md:mt-6 lg:mt-[1.5625rem] text-[clamp(1rem,3vw,1.875rem)] font-light leading-[100%] text-white/70" suppressHydrationWarning>
              <Trans
                i18nKey='Away from the stress and noise of the city, the guesthouse "To Spiti Tis Sofias" is the ideal place for rest and relaxation.'
                components={[
                  <Link
                    key="guesthouse"
                    href={locale === "el" ? "/" : `/${locale}`}
                    className="underline hover:text-white transition-colors decoration-white/50 hover:decoration-white"
                  />,
                ]}
              />
            </p>
            <p className="mt-4 md:mt-6 text-[clamp(1rem,3vw,1.875rem)] font-light leading-[100%] text-white/70" suppressHydrationWarning>
              <Trans
                i18nKey='Just 8 km away from the city of Kastoria, the guesthouse "To Spiti Tis Sofias" is the ideal choice for four-season tourism in the Kastoria region.'
                components={[
                  <Link
                    key="kastoria"
                    href={locale === "el" ? "/area" : `/${locale}/area`}
                    className="underline hover:text-white transition-colors decoration-white/50 hover:decoration-white"
                  />,
                  <Link
                    key="region"
                    href={locale === "el" ? "/area" : `/${locale}/area`}
                    className="underline hover:text-white transition-colors decoration-white/50 hover:decoration-white"
                  />,
                ]}
              />
            </p>
          </div>
        </div>

        <div className="h-[20rem] md:h-[25rem] lg:h-screen order-2 lg:order-1">
          <div 
            ref={mapContainer} 
            id="map" 
            className="w-full h-full" 
          />
        </div>
      </div>
    </section>
  );
}

